import { cn } from "@/lib/utils";
import { Image as ImageIcon, Play } from "lucide-react";

/**
 * Premium, clearly-labeled media placeholder.
 * IMPORTANT: No fake job/customer photos are used on this site. Real media is
 * dropped in later by passing a `src`. Until then this renders an intentional
 * blueprint-style frame with a label.
 *
 * To use a real image/video later:
 *   <MediaPlaceholder src="/media/job-1.jpg" alt="..." />
 *   <MediaPlaceholder type="video" src="/media/move.mp4" poster="/media/move.jpg" />
 */
export const MediaPlaceholder = ({
  label = "Project Photo",
  type = "image",
  icon: Icon,
  className,
  aspect = "aspect-[4/3]",
  src,
  poster,
  alt,
  fit = "cover",
  position = "object-center",
  priority = false,
}) => {
  if (src) {
    const fitClass = fit === "contain" ? "object-contain" : "object-cover";
    if (type === "video") {
      return (
        <video
          src={src}
          poster={poster}
          controls
          playsInline
          preload="none"
          className={cn("block w-full", aspect, fitClass, position, className)}
        />
      );
    }
    return (
      <img
        src={src}
        alt={alt || label}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className={cn("block w-full", aspect, fitClass, position, className)}
      />
    );
  }

  const FallbackIcon = Icon || (type === "video" ? Play : ImageIcon);

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden border border-white/10 bg-ink-elevated",
        aspect,
        className,
      )}
      data-media-placeholder={type}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-ink-elevated via-ink-surface to-ink" />
      <div className="blueprint-grid absolute inset-0 opacity-50" />
      <div className="absolute -left-10 top-0 h-full w-20 rotate-12 bg-gold/[0.035]" />

      {/* corner brackets */}
      <span className="absolute left-3 top-3 h-4 w-4 border-l border-t border-white/20" />
      <span className="absolute right-3 top-3 h-4 w-4 border-r border-t border-white/20" />
      <span className="absolute bottom-3 left-3 h-4 w-4 border-b border-l border-white/20" />
      <span className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-white/20" />

      <div className="relative z-10 flex flex-col items-center gap-3 px-4 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-ink/70 text-gold">
          <FallbackIcon className="h-5 w-5" />
        </span>
        <span>
          <span className="block font-heading text-sm font-bold uppercase tracking-wider text-steel-light">
            {label}
          </span>
          <span className="mt-1 block text-[10px] uppercase tracking-[0.22em] text-steel/70">
            {type === "video" ? "Video coming soon" : "Photo coming soon"}
          </span>
        </span>
      </div>

      {type === "video" && (
        <span className="absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink">
          <Play className="h-4 w-4 fill-current" />
        </span>
      )}
    </div>
  );
};
